import api from '@/api/dataset'

import rtti from '@/sui/tools/rtti'
import dataset from '@/sui/tools/dataset'
import delay from '@/sui/api/delay'

const state = function () {
  return {
    '2019': {},
    '2020': {},
    '2021': {},
    '2022': {},
    '2023': {},
    '2024': {},
    '2025': {},
    '2026': {},

    errors: {
      '2019': {},
      '2020': {},
      '2021': {},
      '2022': {},
      '2023': {},
      '2024': {},
      '2025': {},
      '2026': {}
    },

    names: {
      loaded: false,
      values: {
        '2019': [],
        '2020': [],
        '2021': [],
        '2022': [],
        '2023': [],
        '2024': [],
        '2025': [],
        '2026': []
      }
    },

    dsets: {},
    sheets: {}
  }
}

function apiOrCache (apiName, cacheName, commitName) {
  return ({ state, commit }, request) => {
    if (request.cache && !request.force) {
      const key = dataset.buildKey(
        request.name,
        request.mod,
        request.step
      )
      const cache = state[cacheName][key]
      if (cache != null) {
        return delay(10)({ data: { v: cache } })
      }
    }

    return api[apiName](request).then(
      r => {
        if (r.data.err != null || !request.cache) {
          return r
        }

        commit(
          commitName,
          {
            key: dataset.buildKey(
              request.name,
              request.mod,
              request.step
            ),
            values: r.data.v
          }
        )
        return r
      }
    )
  }
}

// getters
const getters = {
  get: (state, getters) => (year, id) => {
    return state[year][id]
  },

  getAllDatasetNamesByYear: (state) => {
    if (!state.names.loaded) {
      return null
    }
    const result = []
    const v = state.names.values

    for (let y in v) {
      const l = v[y]
      if (l.length > 0) {
        result.push(
          {
            year: y,
            list: l
          }
        )
      }
    }
    return result
  },

  getAllSurveillanceDatasetNamesByYear: (state) => {
    if (!state.names.loaded) {
      return []
    }

    const result = []
    const v = state.names.values
    for (let y in v) {
      const l = v[y].filter(
        d => d.match(/SPIADI[0-9]{4}($|-SURV-)/) != null
      )

      if (l.length > 0) {
        result.push(
          {
            year: y,
            list: l
          }
        )
      }
    }
    return result
  },

  getSheets: (state) => (name, module, step) => {
    const key = dataset.buildKey(name, module, step)
    return state.sheets[key]
  },

  getDatasets: (state) => (name, module, step) => {
    const key = dataset.buildKey(name, module, step)
    return state.dsets[key]
  }
}

// actions
const actions = {
  get ({ state, commit }, request) {
    return api.get(request).then(
      r => {
        if (r.data.err == null) {
          commit(
            'setValue',
            {
              year: request.year,
              id: request.surv,
              values: r.data.den
            }
          )
        }
        return r
      }
    )
  },

  computeAllDatasetsResults () {
    return api.computeAllDatasetsResults()
  },

  isDatasetComputing (_, request) {
    return api.isDatasetComputing()
  },

  transform (_, request) {
    return api.transform(request)
  },

  complete ({ state, commit }, request) {
    const y = `${request.year}`
    return api.complete(request).then(
      r => {
        if (r.data.err == null) {
          for (let i in r.data.v) {
            const d = r.data.v[i]
            commit('setValue', { year: y, id: i, values: d.den })
          }

          for (let i in r.data.e) {
            const e = r.data.e[i]
            commit('setError', { year: y, id: i, values: e })
          }
        }
        return r
      }
    )
  },

  sheets: apiOrCache('sheets', 'sheets', 'setSheets'),
  all: apiOrCache('all', 'dsets', 'allValue'),

  allSpiadiNames ({ state, commit }, region) {
    const key = region ? 'SPIADI____' : 'SPIADI%%'
    commit('resetSpiadiNames')
    return api.names({ key: key }).then(
      r => {
        if (r.data.err == null) {
          commit(
            'allSpiadiNames',
            r.data.v.sort()
          )
        }
        return r
      }
    )
  },

  removeByName ({ state, dispatch }, request) {
    return api.removeByName(request).then(
      r => {
        if (r.data.err == null) {
          dispatch('allSpiadiNames', false)
        }
        return r
      }
    )
  },

  copy ({ state, dispatch }, request) {
    return api.copy(request)
  }
}

// mutations
const mutations = {
  setValue (state, data) {
    state[data.year][data.id] = Object.freeze(data.values)
  },

  setError (state, data) {
    state.errors[data.year][data.id] = data.values
  },

  setSheets (state, data) {
    state.sheets = {
      ...state.sheets,
      [data.key]: Object.freeze(data.values)
    }
  },

  allValue (state, data) {
    state.dsets = {
      ...state.dsets,
      [data.key]: Object.freeze(data.values)
    }
  },

  allSpiadiNames (state, values) {
    for (let i = 0; i !== values.length; ++i) {
      const name = values[i]
      const m = name.match(/^(SPIADI(20[1-2][0-9]).*)/)
      if (m != null) {
        state.names.values[m[2]].push(m[1])
      }
    }
    state.names.loaded = true
  },

  resetSpiadiNames (state) {
    state.names.loaded = false
    const keys = Object.keys(state.names.values)
    for (let i = 0; i !== keys.length; ++i) {
      const n = keys[i]
      if (rtti.isArray(state.names.values[n])) {
        state.names.values[n].length = 0
      }
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
